import clsx from 'clsx'
import React from 'react'

import { makeStyles } from 'tss-react/mui'

import Typography from '@mui/material/Typography'

import { Chip, Link } from '@mui/material'
import ApplicationLogo from 'src/components/icons/ApplicationLogo'
import BrainCircuitIcon from 'src/components/icons/BrainCircuitIcon'
import DatabaseIcon from 'src/components/icons/DatabaseIcon'
import LightningIcon from 'src/components/icons/LightningIcon'
import TrendUpIcon from 'src/components/icons/TrendUpIcon'

export const useStyles = makeStyles()(() => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',

        '@media (max-width: 900px)': {
            flexDirection: 'column',
            height: '100vh',
            overflowY: 'auto'
        }
    },

    leftContainer: {
        flex: 1,
        backgroundColor: '#000',
        padding: '40px 64px',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',

        '@media (max-width: 900px)': {
            order: 2,
            padding: '40px 32px'
        }
    },

    leftLogo: {
        width: 140,
        marginBottom: 16
    },

    leftContentInner: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left'
    },

    leftTopArea: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: '100%',
        paddingTop: 12
    },

    leftTitle: {
        fontSize: '2.0rem',
        fontWeight: 700,
        lineHeight: 1.2
    },

    leftTitleHighlight: {
        color: '#00E300'
    },

    leftSubtitle: {
        marginTop: 6,
        fontSize: '1rem',
        opacity: 0.8
    },

    bottomTitle: {
        marginTop: 70,
        fontSize: '0.6rem',
        fontWeight: 500,
        opacity: 0.6,
        lineHeight: 1.2,
        letterSpacing: '0.3em'
    },

    sectionEyebrow: {
        marginTop: 56,
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        opacity: 0.55
    },

    sectionTitle: {
        fontSize: '2.2rem',
        fontWeight: 700,
        marginTop: 10
    },

    cardsTitle: {
        marginTop: 8,
        fontSize: '1.2rem',
        fontWeight: 700
    },

    cardsSubTitle: {
        fontSize: '0.75rem',
        opacity: 0.4,
        fontWeight: 100
    },

    cardsSubText: {
        marginTop: 15,
        fontSize: '0.8rem',
        lineHeight: 1.6,
        opacity: 0.65
    },

    cardsGrid: {
        marginTop: 10,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 15,
        width: '100%'
    },

    cardBadge: {
        position: 'absolute',
        top: 30,
        right: 20,
        padding: '4px 10px',
        color: '#e6e6e6',
        backgroundColor: '#212229',
        paddingBottom: 4,
        paddingTop: 4,
        fontSize: '0.6rem',
        fontWeight: 500
    },

    cardLink: {
        color: '#00E300',
        fontSize: '0.9rem',
        marginTop: 12,
        fontWeight: 500,
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif !important`,
        cursor: 'pointer'
    },

    infoCard: {
        position: 'relative',
        backgroundColor: '#111',
        borderRadius: 14,
        padding: '16px 32px',
        border: '1px solid #1c1c1c',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 4px 20px rgba(0,0,0,0.35)',
        transition: '0.25s',
        '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0px 6px 28px rgba(0,0,0,0.45)'
        }
    },

    infoCardIconArea: {
        color: '#00E300',
        fontSize: '2.6em'
    },

    footerText: {
        opacity: 0.5,
        fontSize: '0.80rem',
        marginTop: 8
    },

    rightContainer: {
        flex: 1,
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 48px',

        '@media (max-width: 900px)': {
            order: 1,
            width: '100%',
            padding: '32px 24px'
        }
    },

    formWrapper: {
        width: '100%',
        maxWidth: 380,
        display: 'flex',
        flexDirection: 'column',
        gap: 32
    }
}))

export type BaseSigninViewProps = {
    className?: string
    children?: React.ReactNode
}

export function BaseSigninView({ className, children }: BaseSigninViewProps) {
    const { classes } = useStyles()

    return (
        <div className={clsx(classes.root, className)}>
            <div className={classes.leftContainer}>
                <ApplicationLogo
                    className={classes.leftLogo}
                    place="sign-in"
                    sx={{
                        fontSize: '56px !important',
                        width: 'max-content !important'
                    }}
                />

                <div className={classes.leftTopArea}>
                    <div className={classes.leftContentInner}>
                        <Typography className={classes.leftTitle}>
                            Transforme dados complexos em{' '}
                            <span className={classes.leftTitleHighlight}>decisões estratégicas.</span>
                        </Typography>

                        <Typography className={classes.leftSubtitle}>
                            Inteligência de mercado baseada em evidências.
                        </Typography>

                        <Typography className={classes.bottomTitle}>OS 4 PILARES</Typography>

                        <div className={classes.cardsGrid}>
                            <div className={classes.infoCard}>
                                <DatabaseIcon className={classes.infoCardIconArea} fontSize="large" />

                                <Chip size="small" className={classes.cardBadge} label="FUNDAÇÃO" />

                                <Typography className={classes.cardsTitle}>HiperHub</Typography>
                                <Typography className={classes.cardsSubTitle}>Data Lake + Hiperautomação</Typography>

                                <Typography className={classes.cardsSubText}>
                                    Integra, transforma e governa dados de múltiplas fontes. Base semântica que modela
                                    seu negócio.
                                </Typography>
                                <Link
                                    href="https://lifeshub.com.br/produtos/hiper-hub"
                                    rel="noopener"
                                    target="_blank"
                                    underline="none"
                                    className={classes.cardLink}
                                >
                                    {'Conhecer mais >'}
                                </Link>
                            </div>

                            <div className={classes.infoCard}>
                                <BrainCircuitIcon className={classes.infoCardIconArea} fontSize="large" />

                                <Chip size="small" className={classes.cardBadge} label="INTELIGÊNCIA" />

                                <Typography className={classes.cardsTitle}>LIA</Typography>
                                <Typography className={classes.cardsSubTitle}>IA Platform para Processos</Typography>

                                <Typography className={classes.cardsSubText} variant="body1">
                                    Conecta IA e agentes aos seus processos. Automação inteligentecom governança nativa.
                                </Typography>
                                <Link
                                    href="https://lifeshub.com.br/produtos/lia"
                                    rel="noopener"
                                    target="_blank"
                                    underline="none"
                                    className={classes.cardLink}
                                >
                                    {'Conhecer mais >'}
                                </Link>
                            </div>

                            <div className={classes.infoCard}>
                                <TrendUpIcon className={classes.infoCardIconArea} fontSize="large" />
                                <Chip size="small" className={classes.cardBadge} label="ANÁLISE" />

                                <Typography className={classes.cardsTitle}>Insight360</Typography>
                                <Typography className={classes.cardsSubTitle}>Analytics & BI Avançado</Typography>
                                <Typography className={classes.cardsSubText} variant="body1">
                                    Visualizações inteligentes e dashboards em tempo real. Insights adicionáveis para
                                    decisões.
                                </Typography>
                                <Link
                                    href="https://lifeshub.com.br/produtos/insight360"
                                    rel="noopener"
                                    target="_blank"
                                    underline="none"
                                    className={classes.cardLink}
                                >
                                    {'Conhecer mais >'}
                                </Link>
                            </div>

                            <div className={classes.infoCard}>
                                <LightningIcon className={classes.infoCardIconArea} fontSize="large" />

                                <Chip size="small" className={classes.cardBadge} label="EXECUÇÃO" />

                                <Typography className={classes.cardsTitle}>PipelineTurbo</Typography>
                                <Typography className={classes.cardsSubTitle}>Acelerador de Vendas</Typography>
                                <Typography className={classes.cardsSubText} variant="body1">
                                    Prioriza leads, enriquece dados e acelera pipeline. Crescimento previsível e
                                    escalável.
                                </Typography>
                                <Link
                                    href="https://lifeshub.com.br/produtos/pipeline-turbo"
                                    rel="noopener"
                                    target="_blank"
                                    underline="none"
                                    className={classes.cardLink}
                                >
                                    {'Conhecer mais >'}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <Typography className={classes.footerText}>
                    © 2025 LifesHub Intelligence. Todos os direitos reservados.
                </Typography>
            </div>

            <div className={classes.rightContainer}>
                <div className={classes.formWrapper}>{children}</div>
            </div>
        </div>
    )
}
